import React from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/utils/index'
import { CountField } from './components/count-field'
import { GutterFields } from './components/gutter-fields'
import { resolveGutterUnitLabel } from './helpers/guide-unit'
import { validateGuideSettings } from './helpers/guide-settings-validation'
import { useGuideDialogShortcuts } from './hooks/use-guide-dialog-shortcuts'
import { useGuideSettingsApply } from './hooks/use-guide-settings-apply'
import { useGuideSettingsForm } from './hooks/use-guide-settings-form'
import { useLinkedGutterHandlers } from './hooks/use-linked-gutter-handlers'
import type { ColumnsRowsSettings } from './types/columns-rows-settings'
import type { GuideUnit } from './types/guide-unit'

type ColumnsRowsGuidesDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  unit: GuideUnit
  settings: ColumnsRowsSettings
  defaultGuttersLinked: boolean
  pageInnerWidth: number
  pageInnerHeight: number
  onSettingsChange: (settings: ColumnsRowsSettings) => void | Promise<void>
}

const ColumnsRowsGuidesDialog: React.FC<ColumnsRowsGuidesDialogProps> = ({
  open,
  onOpenChange,
  unit,
  settings,
  defaultGuttersLinked,
  pageInnerWidth,
  pageInnerHeight,
  onSettingsChange,
}) => {
  const form = useGuideSettingsForm({
    open,
    unit,
    settings,
    defaultGuttersLinked,
    pageInnerWidth,
    pageInnerHeight,
  })
  const { columnsInput, rowsInput, colGutterInput, rowGutterInput } = form

  useGuideDialogShortcuts(open)

  const validation = React.useMemo(
    () =>
      validateGuideSettings(
        { columnsInput, rowsInput, colGutterInput, rowGutterInput },
        unit,
        pageInnerWidth,
        pageInnerHeight
      ),
    [columnsInput, rowsInput, colGutterInput, rowGutterInput, unit, pageInnerWidth, pageInnerHeight]
  )
  const { previewSettings } = validation

  const handleCancel = React.useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  const gutterHandlers = useLinkedGutterHandlers({
    unit,
    guttersLinked: form.guttersLinked,
    setGuttersLinked: form.setGuttersLinked,
    colGutterInput,
    rowGutterInput,
    setColGutterInput: form.setColGutterInput,
    setRowGutterInput: form.setRowGutterInput,
    columns: validation.columns,
    rows: validation.rows,
    pageInnerWidth,
    pageInnerHeight,
  })

  const handleApply = useGuideSettingsApply({
    previewSettings,
    onSettingsChange,
    onOpenChange,
    setApplyError: form.setApplyError,
    setIsApplying: form.setIsApplying,
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        onPointerDownOutside={(event) => event.preventDefault()}
        className="w-[425px] max-w-[calc(100%-2rem)] rounded-[10px] border border-[var(--base-border,#E5E5E5)] bg-[var(--base-background,#FFF)] p-6 shadow-lg"
      >
        <DialogHeader className="w-full gap-[6px] pr-8">
          <DialogTitle className="text-[18px] leading-[18px] font-semibold tracking-[-0.09px] text-[var(--base-foreground,#0A0A0A)]">
            Guide settings
          </DialogTitle>
          <DialogDescription className="text-sm leading-5 font-normal text-[var(--base-muted-foreground,#737373)]">
            Column and row guides
          </DialogDescription>
        </DialogHeader>
        <DialogClose className="absolute top-4 right-4 rounded-[2px] p-0 text-[var(--base-muted-foreground,#737373)] opacity-70 transition-colors hover:bg-accent hover:text-[var(--base-foreground,#0A0A0A)]">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogClose>

        <div className="w-full self-stretch px-6 py-6">
          <div className="grid gap-y-8">
            <div className="grid grid-cols-2 gap-8">
              <CountField
                heading="Columns per page"
                label="Column count"
                inputId="columns-per-page-input"
                value={columnsInput}
                onChange={form.setColumnsInput}
                message={validation.columnCountMessage}
              />
              <CountField
                heading="Rows per page"
                label="Row count"
                inputId="rows-per-spread-input"
                value={rowsInput}
                onChange={form.setRowsInput}
                message={validation.rowCountMessage}
              />
            </div>

            <GutterFields
              unitLabel={resolveGutterUnitLabel(unit)}
              colGutterInput={colGutterInput}
              rowGutterInput={rowGutterInput}
              colGutterMessage={validation.colGutterMessage}
              rowGutterMessage={validation.rowGutterMessage}
              guttersLinked={form.guttersLinked}
              onColumnGutterChange={gutterHandlers.handleColumnGutterChange}
              onRowGutterChange={gutterHandlers.handleRowGutterChange}
              onColumnGutterBlur={gutterHandlers.handleColumnGutterBlur}
              onRowGutterBlur={gutterHandlers.handleRowGutterBlur}
              onToggleGutterLink={gutterHandlers.handleToggleGutterLink}
            />
          </div>
        </div>

        <DialogFooter className="w-full items-end gap-3">
          <p
            className={cn(
              'w-full text-xs text-destructive sm:mr-auto',
              form.applyError ? 'visible' : 'invisible'
            )}
          >
            {form.applyError ?? '.'}
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="h-9 rounded-md border border-[var(--base-border,#E5E5E5)] bg-[var(--base-background,#FFF)] px-4 py-2 text-sm font-medium text-[var(--base-foreground,#0A0A0A)] shadow-xs hover:bg-accent hover:text-accent-foreground"
              disabled={form.isApplying}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              disabled={previewSettings == null || form.isApplying}
              className="h-9 rounded-md bg-[var(--base-primary,#144087)] px-4 py-2 text-sm font-medium text-[var(--base-primary-foreground,#FAFAFA)] shadow-xs hover:bg-[var(--base-primary,#144087)]/90 disabled:bg-[var(--base-primary,#144087)]/45 disabled:text-[var(--base-primary-foreground,#FAFAFA)]"
            >
              Apply
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ColumnsRowsGuidesDialog
